import React, { useEffect, useId, useMemo, useState } from "react";
import { UserPlus } from "lucide-react";
import {
  AppButton, AppModal, Callout, FormField, Input, NativeSelect,
} from "../ui";

/**
 * Turn a WhatsApp chat into an enquiry (vc_be POST /enquiries/from-whatsapp/:id).
 *
 * The mobile is the chat's own number and is not editable here — that is the
 * point: the lead is the person who wrote to us, never the institute's number.
 * Name is required; email and course are optional.
 *
 * When the number already has an enquiry the server answers 409 and this asks
 * what to do: link the chat to that enquiry, or create a new one anyway. A lead
 * that belongs to another counsellor is not shown (ownership control), so only
 * "create anyway" is offered for it.
 */

const looksLikePhone = (s) => /^[+\d\s()-]{6,}$/.test(String(s || "").trim());

const fmtDate = (value) => {
  if (!value) return "";
  try {
    return new Date(value).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return "";
  }
};

export default function ConvertToEnquiryModal({ show, conversation, loadCourses, onConvert, onDone, onHide }) {
  const nameId = useId();
  const emailId = useId();
  const courseId = useId();

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [course, setCourse] = useState("");
  const [courses, setCourses] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);       // { field?, message }
  const [existing, setExisting] = useState(null); // { code, enquiry?, message }

  useEffect(() => {
    if (!show) return;
    // The WhatsApp profile name is a fair start; a bare number is not a name.
    setFullName(looksLikePhone(conversation?.name) ? "" : (conversation?.name || ""));
    setEmail("");
    setCourse("");
    setError(null);
    setExisting(null);
    let alive = true;
    Promise.resolve(loadCourses ? loadCourses() : [])
      .then((list) => { if (alive) setCourses(list || []); })
      .catch(() => { if (alive) setCourses([]); });
    return () => { alive = false; };
  }, [show, conversation?._id, conversation?.name, loadCourses]);

  const courseGroups = useMemo(() => {
    const byName = (a, b) => (a.name || "").localeCompare(b.name || "");
    return [
      { label: "Live Courses", items: courses.filter((c) => c.courseType !== "self-paced").sort(byName) },
      { label: "Self-Paced Courses", items: courses.filter((c) => c.courseType === "self-paced").sort(byName) },
    ].filter((g) => g.items.length > 0);
  }, [courses]);

  const submit = async (existingChoice) => {
    if (!fullName.trim()) {
      setError({ field: "fullName", message: "Enter the student's name." });
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const data = await onConvert({
        fullName: fullName.trim(),
        email: email.trim(),
        courseId: course || null,
        ...(existingChoice ? { existing: existingChoice } : {}),
      });
      onDone(data.enquiry, { linked: !!data.linked, message: data.message });
    } catch (err) {
      const body = err?.response?.data || {};
      if (err?.response?.status === 409) {
        setExisting({ code: body.code, enquiry: body.enquiry || null, message: body.message });
      } else {
        setError({ field: body.field, message: body.message || "Could not create the enquiry." });
      }
    }
    setSaving(false);
  };

  const footer = existing ? (
    <div className="tw-flex tw-flex-wrap tw-justify-end tw-gap-2">
      <AppButton variant="ghost" onClick={() => setExisting(null)} disabled={saving}>Back</AppButton>
      <AppButton variant="outline" onClick={() => submit("new")} disabled={saving}>
        Create a new enquiry anyway
      </AppButton>
      {existing.enquiry && (
        <AppButton onClick={() => submit("link")} disabled={saving}>
          {saving ? "Linking…" : "Link this chat to it"}
        </AppButton>
      )}
    </div>
  ) : (
    <div className="tw-flex tw-justify-end tw-gap-2">
      <AppButton variant="ghost" onClick={onHide} disabled={saving}>Cancel</AppButton>
      <AppButton onClick={() => submit(null)} disabled={saving}>
        {saving ? "Creating…" : "Create enquiry"}
      </AppButton>
    </div>
  );

  return (
    <AppModal
      show={show}
      onHide={saving ? undefined : onHide}
      title="Convert chat to enquiry"
      icon={<UserPlus size={18} aria-hidden="true" />}
      size="md"
      footer={footer}
    >
      <div className="tw-flex tw-flex-col tw-gap-4">
        <div className="tw-flex tw-flex-col tw-gap-0.5">
          <span className="tw-text-xs tw-font-medium tw-text-content-muted">Mobile (from this chat)</span>
          <span className="tw-text-sm tw-font-semibold tw-text-content">+{String(conversation?.phone || "").replace(/^\+/, "")}</span>
          <span className="tw-text-xs tw-text-content-subtle">The enquiry is saved on the number this person is chatting from.</span>
        </div>

        {existing ? (
          <Callout tone="warning" role="alert">
            {existing.enquiry ? (
              <>
                This number already has an enquiry — <strong>{existing.enquiry.fullName || "Unnamed lead"}</strong>
                {existing.enquiry.status ? ` · ${existing.enquiry.status}` : ""}
                {existing.enquiry.createdAt ? ` · added ${fmtDate(existing.enquiry.createdAt)}` : ""}.
                {" "}Link this chat to it, or create a separate enquiry.
              </>
            ) : (
              existing.message
            )}
          </Callout>
        ) : (
          <>
            {error && !error.field && <Callout tone="danger" role="alert">{error.message}</Callout>}
            <FormField label="Student name" htmlFor={nameId} required error={error?.field === "fullName" ? error.message : undefined}>
              <Input id={nameId} value={fullName} onChange={(e) => setFullName(e.target.value)} maxLength={120} autoFocus />
            </FormField>
            <FormField label="Email" htmlFor={emailId} hint="Optional" error={error?.field === "email" ? error.message : undefined}>
              <Input id={emailId} type="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={200} />
            </FormField>
            <FormField label="Course" htmlFor={courseId} hint="Optional" error={error?.field === "courseId" ? error.message : undefined}>
              <NativeSelect id={courseId} value={course} onChange={(e) => setCourse(e.target.value)}>
                <option value="">Not decided yet</option>
                {courseGroups.map((g) => (
                  <optgroup key={g.label} label={g.label}>
                    {g.items.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
                  </optgroup>
                ))}
              </NativeSelect>
            </FormField>
          </>
        )}
      </div>
    </AppModal>
  );
}
